import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiClock, FiDollarSign, FiArrowRight } from 'react-icons/fi';
import { servicesAPI } from '../api';
import SEO from '../components/seo/SEO';
import { breadcrumb } from '../components/seo/jsonLd';

const FALLBACK = [
  { id:1, name:'Classic Haircut', category:'Hair', duration:45, price:35, description:'Precision cut tailored to your face shape and lifestyle.', image:null },
  { id:2, name:'Balayage & Highlights', category:'Color', duration:120, price:120, description:'Sun-kissed color with seamless blending for a natural glow.', image:null },
  { id:3, name:'Bridal Makeup', category:'Makeup', duration:90, price:180, description:'Flawless bridal look designed to last all day and night.', image:null },
  { id:4, name:'Deep Conditioning', category:'Hair', duration:60, price:55, description:'Intensive moisture treatment to restore shine and vitality.', image:null },
  { id:5, name:'Manicure & Nail Art', category:'Nails', duration:60, price:45, description:'Beautiful nail care with custom art and gel finish.', image:null },
  { id:6, name:'Facial Treatment', category:'Skin', duration:75, price:85, description:'Revitalizing facial using premium skincare products.', image:null },
  { id:7, name:'Eyebrow Threading', category:'Brows', duration:20, price:18, description:'Precise shaping for perfectly defined brows.', image:null },
  { id:8, name:'Keratin Treatment', category:'Hair', duration:150, price:200, description:'Smoothing treatment for frizz-free, silky hair.', image:null },
];

const CATEGORY_COLORS = {
  Hair:'bg-salon-primary/10 text-salon-primary', Color:'bg-salon-secondary/10 text-salon-secondary',
  Makeup:'bg-pink-100 text-pink-700', Nails:'bg-purple-100 text-purple-700',
  Skin:'bg-green-100 text-green-700', Brows:'bg-orange-100 text-orange-700',
};

export default function Services() {
  const [activeCategory, setActiveCategory] = useState('All');

  const { data: services = FALLBACK } = useQuery({
    queryKey: ['services'],
    queryFn: () => servicesAPI.getAll().then(r => r.data.data || r.data),
    placeholderData: FALLBACK,
  });

  const categories = ['All', ...new Set(services.map(s => s.category))];
  const filtered = activeCategory === 'All' ? services : services.filter(s => s.category === activeCategory);

  return (
    <>
      <SEO
        title="Salon Services in Chennai – Hair, Skin, Nails, Makeup, Wellness"
        description="Browse Salon Vora's full menu — haircuts, color, balayage, bridal makeup, facials, manicures, keratin and more. Transparent prices and online booking."
        path="/services"
        jsonLd={breadcrumb([
          { name: 'Home', url: '/' },
          { name: 'Services', url: '/services' },
        ])}
      />
    <div className="min-h-screen bg-salon-cream">
      {/* Hero */}
      <section className="relative bg-hero-gradient py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-30"
          style={{ backgroundImage: 'radial-gradient(circle at 70% 50%, #8B1A4A 0%, transparent 60%)' }} />
        <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-salon-primary/30 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-40 -right-40 w-[400px] h-[400px] rounded-full bg-salon-secondary/15 blur-3xl pointer-events-none" />
        <div className="container mx-auto px-4 relative z-10 text-center">
          <motion.span initial={{ opacity:0, y:20 }} animate={{ opacity:1, y:0 }}
            className="section-tag mb-4 inline-block">Our Services</motion.span>
          <motion.h1 initial={{ opacity:0, y:30 }} animate={{ opacity:1, y:0 }} transition={{ delay:.1 }}
            className="font-display text-4xl md:text-6xl text-white mb-6">
            Beauty Treatments<br /><span className="text-salon-secondary">Curated for You</span>
          </motion.h1>
          <motion.p initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay:.2 }}
            className="text-white/70 text-lg max-w-2xl mx-auto">
            From signature haircuts to luxurious skin treatments — every service is crafted to bring out your best self.
          </motion.p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="sticky top-16 z-30 bg-white border-b border-salon-cream-dark shadow-sm">
        <div className="container mx-auto px-4 py-4 flex gap-2 overflow-x-auto scrollbar-none">
          {categories.map(cat => (
            <button key={cat} onClick={() => setActiveCategory(cat)}
              className={`px-5 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all
                ${activeCategory === cat ? 'bg-salon-primary text-white shadow-salon-md' : 'bg-salon-cream text-salon-muted hover:bg-salon-cream-dark'}`}>
              {cat}
            </button>
          ))}
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 container mx-auto px-4">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filtered.map((service, i) => (
            <motion.div key={service.id}
              initial={{ opacity:0, y:20 }} animate={{ opacity:1, y:0 }} transition={{ delay: i*0.05 }}
              className="card group overflow-hidden">
              <div className="h-48 bg-gradient-to-br from-salon-cream-dark to-salon-cream flex items-center justify-center text-6xl">
                {service.image
                  ? <img src={service.image} alt={service.name} className="w-full h-full object-cover" />
                  : '✂️'}
              </div>
              <div className="p-5">
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${CATEGORY_COLORS[service.category] || 'bg-gray-100 text-gray-600'}`}>
                  {service.category}
                </span>
                <h3 className="font-display text-xl text-salon-dark mt-3 mb-2 group-hover:text-salon-primary transition-colors">
                  {service.name}
                </h3>
                <p className="text-salon-muted text-sm mb-4 line-clamp-2">{service.description}</p>
                <div className="flex items-center justify-between text-sm text-salon-muted mb-4">
                  <span className="flex items-center gap-1"><FiClock />{service.duration} min</span>
                  <span className="flex items-center gap-1 font-semibold text-salon-primary text-base">
                    <FiDollarSign />{service.price}
                  </span>
                </div>
                <Link to={`/book?service=${service.id}`}
                  className="btn-primary w-full text-center flex items-center justify-center gap-2">
                  Book Now <FiArrowRight />
                </Link>
              </div>
            </motion.div>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-20 text-salon-muted">No services found in this category.</div>
        )}
      </section>

      {/* CTA */}
      <section className="bg-salon-primary py-20 text-center">
        <div className="container mx-auto px-4">
          <h2 className="font-display text-3xl md:text-4xl text-white mb-4">Not Sure What You Need?</h2>
          <p className="text-white/70 mb-8">Our expert stylists will help you find the perfect treatment.</p>
          <Link to="/contact" className="btn-gold">Book a Consultation</Link>
        </div>
      </section>
    </div>
    </>
  );
}
